import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AdminLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, "Login failed. Please check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain-overlay relative flex min-h-screen items-center justify-center bg-navy-950 px-6" data-testid="admin-login-page">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-navy-900 p-10 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <LogoMark className="h-16 w-auto rounded-xl" />
          <h1 className="mt-5 font-serif text-2xl font-bold text-white">Admin Console</h1>
          <p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">SSS GROUPS</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="admin-email" className="mb-1.5 block text-sm font-semibold text-slate-300">
              Email Address
            </Label>
            <Input
              id="admin-email"
              type="email"
              required
              data-testid="admin-email-input"
              className="h-12 border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-gold"
              placeholder="admin@sssgroups.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-slate-300">
              Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              required
              data-testid="admin-password-input"
              className="h-12 border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-gold"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold" data-testid="admin-back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;
